import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { DeliveryStatusBadge } from '@/features/patient-record/components/_shared/QuestionnaireDeliveryStatus';
import type { DeliveryStatus } from '@/features/patient-record/types';
import { formatDate } from '@/lib/format';

import { useBulkSendSummary, type BulkSendJobViewModel } from '../api';
import type { BulkSendPatientFilter } from '../filterRequest';

const EMPTY = '—';

/** Safely decode the opaque wizard-filter JSON captured at launch. */
function decodeFilter(json: string | null): Partial<BulkSendPatientFilter> | null {
  if (json === null || json.trim().length === 0) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Expanded-row content for one bulk-send job (PTV2-1162): the immutable request snapshot (template,
 * expiry, send method, creator, the wizard filter that built the cohort) plus the live per-recipient
 * results (counts + problem items) fetched lazily when the row opens.
 */
const SUMMARY_POLL_MS = 10000;

export function BulkSendJobDetails({
  job,
  creatorName,
  autoRefresh = false,
}: {
  readonly job: BulkSendJobViewModel;
  readonly creatorName: string;
  readonly autoRefresh?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const summaryQuery = useBulkSendSummary(job.jobId, autoRefresh ? SUMMARY_POLL_MS : false);
  const summary = summaryQuery.data;

  const fieldPrefix = 'routes.questionnaires.bulkSend.create.fields';
  const filter = decodeFilter(job.filterCriteria);
  const criteria: { readonly label: string; readonly value: string }[] = [];
  if (filter !== null) {
    if (filter.lastName !== undefined && filter.lastName.length > 0) {
      criteria.push({ label: t(`${fieldPrefix}.lastName`), value: filter.lastName });
    }
    if (filter.firstName !== undefined && filter.firstName.length > 0) {
      criteria.push({ label: t(`${fieldPrefix}.firstName`), value: filter.firstName });
    }
    if (filter.dobFrom !== undefined && filter.dobFrom.length > 0) {
      criteria.push({ label: t(`${fieldPrefix}.dobFrom`), value: filter.dobFrom });
    }
    if (filter.dobTo !== undefined && filter.dobTo.length > 0) {
      criteria.push({ label: t(`${fieldPrefix}.dobTo`), value: filter.dobTo });
    }
    if (filter.sex !== undefined && filter.sex.length > 0) {
      criteria.push({
        label: t(`${fieldPrefix}.sex`),
        value: t(`routes.questionnaires.bulkSend.create.sexOptions.${filter.sex}`),
      });
    }
    if (filter.lifeStatus !== undefined && filter.lifeStatus.length > 0) {
      criteria.push({
        label: t(`${fieldPrefix}.lifeStatus`),
        value: t(`routes.questionnaires.bulkSend.create.lifeStatusOptions.${filter.lifeStatus}`),
      });
    }
  }

  return (
    <div className="space-y-4 py-1">
      {/* Request snapshot ("what was requested"). */}
      <section>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('routes.questionnaires.bulkSend.detail.requestHeading')}
        </h3>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">{t('routes.questionnaires.bulkSend.detail.template')}</dt>
          <dd className="text-foreground">{job.templateName ?? (job.templateId.length > 0 ? job.templateId : EMPTY)}</dd>

          <dt className="text-muted-foreground">{t('routes.questionnaires.bulkSend.detail.sendMethod')}</dt>
          <dd className="text-foreground">{job.sendMethod.length > 0 ? job.sendMethod : EMPTY}</dd>

          <dt className="text-muted-foreground">{t('routes.questionnaires.bulkSend.detail.createdBy')}</dt>
          <dd className="text-foreground">{creatorName}</dd>

          <dt className="text-muted-foreground">{t('routes.questionnaires.bulkSend.detail.expiresAt')}</dt>
          <dd className="text-foreground">{job.expiresAt !== null ? formatDate(job.expiresAt) : EMPTY}</dd>

          <dt className="text-muted-foreground">{t('routes.questionnaires.bulkSend.detail.filter')}</dt>
          <dd className="text-foreground">
            {criteria.length === 0 ? (
              <span className="text-muted-foreground">
                {t('routes.questionnaires.bulkSend.detail.filterAll')}
              </span>
            ) : (
              <ul className="flex flex-wrap gap-x-4 gap-y-0.5">
                {criteria.map((c) => (
                  <li key={c.label}>
                    <span className="text-muted-foreground">{c.label}: </span>
                    {c.value}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </dl>
      </section>

      {/* Live results ("what happened"). */}
      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('routes.questionnaires.bulkSend.detail.resultsHeading')}
        </h3>

        {summaryQuery.isPending && (
          <p className="text-sm text-muted-foreground">{t('routes.questionnaires.bulkSend.summary.loading')}</p>
        )}

        {summaryQuery.isError && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.questionnaires.bulkSend.summary.error', { code: getErrorCodeForUi(summaryQuery.error) })}
          </p>
        )}

        {summary !== undefined && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {summary.counts.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  {t('routes.questionnaires.bulkSend.summary.total', { count: summary.total })}
                </span>
              ) : (
                summary.counts.map((c) => (
                  <span key={c.status} className="inline-flex items-center gap-1.5 text-sm">
                    <DeliveryStatusBadge status={c.status as DeliveryStatus} />
                    <span className="tabular-nums font-medium text-foreground">{c.count}</span>
                  </span>
                ))
              )}
            </div>

            {summary.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('routes.questionnaires.bulkSend.summary.noProblems')}
              </p>
            ) : (
              <DataTable aria-label={t('routes.questionnaires.bulkSend.summary.itemsHeading')}>
                <DataTableTable aria-label={t('routes.questionnaires.bulkSend.summary.itemsHeading')}>
                  <DataTableThead>
                    <DataTableHeaderRow>
                      <DataTableTh widthClassName="w-1/3">
                        {t('routes.questionnaires.bulkSend.summary.items.recipient')}
                      </DataTableTh>
                      <DataTableTh widthClassName="w-28">
                        {t('routes.questionnaires.bulkSend.summary.items.status')}
                      </DataTableTh>
                      <DataTableTh>{t('routes.questionnaires.bulkSend.summary.items.detail')}</DataTableTh>
                    </DataTableHeaderRow>
                  </DataTableThead>
                  <DataTableTbody>
                    {summary.items.map((item, idx) => (
                      <DataTableTr key={`${item.entityGuid}-${String(idx)}`}>
                        <DataTableTd className="text-foreground">
                          {item.recipient.length > 0 ? item.recipient : item.entityGuid || EMPTY}
                        </DataTableTd>
                        <DataTableTd>
                          {item.status.length > 0 ? (
                            <DeliveryStatusBadge status={item.status as DeliveryStatus} />
                          ) : (
                            EMPTY
                          )}
                        </DataTableTd>
                        <DataTableTd className="text-muted-foreground">
                          {item.errorMessage.length > 0
                            ? item.errorMessage
                            : item.errorCode.length > 0
                              ? item.errorCode
                              : EMPTY}
                        </DataTableTd>
                      </DataTableTr>
                    ))}
                  </DataTableTbody>
                </DataTableTable>
              </DataTable>
            )}
          </>
        )}
      </section>
    </div>
  );
}
